import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';
import { useMemo } from 'react';
import { POR_PAGINA_PADRAO } from '../services/usuariosService';
import type { FiltrosUsuarios } from '../types/Usuario';

export const PERFIS = ['administrador', 'profissional'] as const;
export const SIM_NAO = ['sim', 'nao'] as const;
export const VINCULOS = [
  'vinculado',
  'sem_correspondencia',
  'duplicado',
  'nao_verificado',
] as const;
const ORDENAR = ['nome', 'email', 'ultimoLogin'] as const;

const texto = parseAsString.withDefault('');
const parsers = {
  nome: texto,
  email: texto,
  perfil: parseAsArrayOf(parseAsStringLiteral(PERFIS)).withDefault([]),
  entrou: parseAsArrayOf(parseAsStringLiteral(SIM_NAO)).withDefault([]),
  ultimoLoginDe: texto,
  ultimoLoginAte: texto,
  vinculo: parseAsArrayOf(parseAsStringLiteral(VINCULOS)).withDefault([]),
  ordenar: parseAsStringLiteral(ORDENAR),
  direcao: parseAsStringLiteral(['asc', 'desc']).withDefault('asc'),
  pagina: parseAsInteger.withDefault(1),
  porPagina: parseAsInteger.withDefault(POR_PAGINA_PADRAO),
};

/** Os que não contam como "filtro aplicado" (ordem e página). */
const FORA_DOS_FILTROS = ['ordenar', 'direcao', 'pagina', 'porPagina'] as const;
const vazio = (v: unknown) => v === '' || (Array.isArray(v) && v.length === 0);

/** Filtros, ordenação e página da lista na URL (spec 35 R9). Qualquer mudança, menos de página, volta a `pagina` para 1. */
export function useFiltrosUsuarios() {
  const [bruto, setBruto] = useQueryStates(parsers);

  const filtros = useMemo<FiltrosUsuarios>(
    () => ({ ...bruto, ordenar: bruto.ordenar ?? '' }),
    [bruto],
  );

  /** `''` e `[]` saem da URL. */
  const alterar = (parcial: Partial<FiltrosUsuarios>) => {
    const limpo = Object.fromEntries(
      Object.entries(parcial).map(([k, v]) => [k, vazio(v) ? null : v]),
    );
    return setBruto({ ...limpo, ...('pagina' in parcial ? {} : { pagina: null }) });
  };

  const limparFiltros = () =>
    setBruto({
      ...Object.fromEntries(
        Object.keys(parsers)
          .filter((k) => !(FORA_DOS_FILTROS as readonly string[]).includes(k))
          .map((k) => [k, null]),
      ),
      pagina: null,
    });

  const filtrosAtivos = Object.entries(filtros).some(
    ([k, v]) => !(FORA_DOS_FILTROS as readonly string[]).includes(k) && !vazio(v),
  );

  return { filtros, alterar, limparFiltros, filtrosAtivos };
}
